import React, { useState, useRef, useEffect } from 'react';
import './AudioPlayer.css';

function PlayIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
            <path d="M3 2.5v9l8-4.5-8-4.5z" />
        </svg>
    );
}

function PauseIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
            <rect x="2.5" y="2" width="3" height="10" rx="0.75" />
            <rect x="8.5" y="2" width="3" height="10" rx="0.75" />
        </svg>
    );
}

function VolumeOnIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M3 6.5v5h3l4 3V3.5L6 6.5H3z" strokeLinejoin="round" />
            <path d="M12.5 6.5a3 3 0 010 5" strokeLinecap="round" />
            <path d="M14 4.5a5.5 5.5 0 010 9" strokeLinecap="round" />
        </svg>
    );
}

function VolumeOffIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M3 6.5v5h3l4 3V3.5L6 6.5H3z" strokeLinejoin="round" />
            <path d="M13 7l4 4M17 7l-4 4" strokeLinecap="round" />
        </svg>
    );
}

const AudioPlayer = ({ src, style = {} }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const volumeBeforeMute = useRef(1);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [src]);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;

        const seekTime = (e.target.value / 100) * duration;
        audio.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isMuted) {
            const restored = volumeBeforeMute.current || 1;
            audio.volume = restored;
            setVolume(restored);
            setIsMuted(false);
        } else {
            volumeBeforeMute.current = volume;
            audio.volume = 0;
            setIsMuted(true);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className="audio-player" style={style}>
            <audio ref={audioRef} src={src} preload="metadata" />

            <button
                type="button"
                className="audio-player__play"
                onClick={togglePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
            >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <div className="audio-player__track-wrap">
                <input
                    type="range"
                    className="audio-player__seek"
                    min="0"
                    max="100"
                    step="0.1"
                    value={progress}
                    onChange={handleSeek}
                    aria-label="Seek"
                    style={{ '--progress': `${progress}%` }}
                />
                <span className="audio-player__time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
            </div>

            <button
                type="button"
                className="audio-player__volume"
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
                {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
            </button>
        </div>
    );
};

export default AudioPlayer;
